import { useState, type CSSProperties, type ReactNode, type Ref } from 'react'
import { Link } from '@tanstack/react-router'
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
} from '@dnd-kit/core'
import { restrictToParentElement } from '@dnd-kit/modifiers'
import {
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { Location } from '@mads/shared'
import { ConfirmDialog, ErrorNote, Loading } from '@/components/Feedback'
import {
  useCreateLocation,
  useDeleteLocation,
  useLocations,
  useReorderLocations,
} from '@/lib/queries'
import { useIsAdmin } from '@/lib/session'
import { moveInOrder } from '@/lib/walkingOrder'

/** The grip. Decorative — the button around it carries the label. */
function GripDots() {
  return (
    <svg width="12" height="18" viewBox="0 0 12 18" aria-hidden="true" focusable="false">
      {[3, 9, 15].map((y) =>
        [3.5, 8.5].map((x) => (
          <circle key={`${x}-${y}`} cx={x} cy={y} r="1.4" fill="currentColor" />
        )),
      )}
    </svg>
  )
}

interface CardProps {
  location: Location
  /** Its place in the walk, which is what the number on the card means. */
  index: number
  isAdmin: boolean
  onRemove: (location: Location) => void
}

/**
 * One location. `handle` is the drag grip and is absent for anyone who cannot
 * reorder, so crew get the same card without a control that would refuse them.
 */
function LocationCard({
  location,
  index,
  isAdmin,
  onRemove,
  handle,
  ref,
  style,
  dragging = false,
}: CardProps & {
  handle?: ReactNode
  ref?: Ref<HTMLDivElement>
  style?: CSSProperties
  dragging?: boolean
}) {
  return (
    <div className={`card location-card${dragging ? ' is-dragging' : ''}`} ref={ref} style={style}>
      <div className="location-card-head">
        {handle}
        <span className="location-order" aria-hidden="true">
          {index + 1}
        </span>
        <div className="card-kicker" style={{ margin: 0 }}>
          {location.itemCount} item{location.itemCount === 1 ? '' : 's'}
        </div>
      </div>

      <div className="card-title">{location.name}</div>
      <p className="card-body">
        {location.unitCount} unit{location.unitCount === 1 ? '' : 's'} total
        {location.description !== null && <> · {location.description}</>}
      </p>

      <div className="action-row">
        {/* Lands on the inventory already filtered to this place. */}
        <Link
          to="/inventory"
          className="btn btn-ghost"
          style={{ paddingLeft: 0 }}
          search={{ locationId: location.id }}
        >
          View items
        </Link>

        {isAdmin && (
          <>
            {/*
             * Renaming used to happen in the card. It moved to its own
             * page so the description has somewhere to live (issue #7).
             */}
            <Link
              to="/locations/$locationId"
              className="btn btn-ghost"
              params={{ locationId: String(location.id) }}
            >
              Edit
            </Link>
            <button type="button" className="btn btn-ghost" onClick={() => onRemove(location)}>
              Remove
            </button>
          </>
        )}
      </div>
    </div>
  )
}

/**
 * The draggable card (issue #27). Only the grip carries the drag listeners:
 * the card is full of links and buttons, and making the whole thing draggable
 * would swallow the clicks meant for them.
 */
function SortableLocationCard(props: CardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: props.location.id,
  })

  return (
    <LocationCard
      {...props}
      ref={setNodeRef}
      dragging={isDragging}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        // Lifted clear of the cards it is being dragged across.
        zIndex: isDragging ? 1 : undefined,
      }}
      handle={
        <button
          type="button"
          className="drag-handle"
          aria-label={`Reorder ${props.location.name}`}
          {...attributes}
          {...listeners}
        >
          <GripDots />
        </button>
      }
    />
  )
}

/**
 * What removing a location actually does, since issue #9: the place goes to the
 * trash, and its kit goes with it rather than being detached. Saying so is the
 * difference between an admin expecting to lose the shelf assignments and
 * knowing a restore brings them all back.
 */
function removalBody(location: Location): string {
  if (location.itemCount === 0) {
    return `"${location.name}" moves to the trash, where it can be restored.`
  }

  const one = location.itemCount === 1
  const items = `${location.itemCount} item${one ? '' : 's'}`
  // The verbs have to agree with the count: "its 1 item stays … and counts".
  const stay = one ? 'stays' : 'stay'
  const count = one ? 'counts' : 'count'
  const them = one ? 'it' : 'them'

  return `"${location.name}" moves to the trash, where it can be restored. Its ${items} ${stay} in the inventory and ${count} as unassigned until it comes back — restoring the location puts ${them} back on it.`
}

/**
 * Create and remove the named places items can live in (spec §3.2); editing one
 * is its own page. The management controls are admin-only (spec W-05); everyone
 * can see where things are, and in what order they are walked.
 */
export function Locations() {
  const isAdmin = useIsAdmin()
  const { data: locations, isPending, error } = useLocations()

  const create = useCreateLocation()
  const remove = useDeleteLocation()
  const reorder = useReorderLocations()

  const [newName, setNewName] = useState('')
  const [confirming, setConfirming] = useState<Location | null>(null)

  // Pointer drags need a little travel before they start, so a click that
  // lands on the grip by accident stays a click. The keyboard sensor is what
  // makes the order reachable without a mouse: space to lift, arrows to move.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  if (isPending) return <Loading what="Loading locations" />
  if (error !== null) return <ErrorNote error={error} />

  const ids = locations.map((location) => location.id)
  const nameOf = (id: number | string) =>
    locations.find((location) => location.id === Number(id))?.name ?? 'The location'
  const placeOf = (id: number | string) => ids.indexOf(Number(id)) + 1

  /*
   * The defaults would announce "item 7" — dnd-kit's ids are the location ids,
   * which mean nothing read aloud. These say the name and the place in the walk.
   */
  const announcements: Announcements = {
    onDragStart: ({ active }) =>
      `Picked up ${nameOf(active.id)}, ${placeOf(active.id)} of ${ids.length}.`,
    onDragOver: ({ active, over }) =>
      over === null ? undefined : `${nameOf(active.id)} is over place ${placeOf(over.id)}.`,
    onDragEnd: ({ active, over }) =>
      over === null
        ? `${nameOf(active.id)} was dropped.`
        : `${nameOf(active.id)} moved to place ${placeOf(over.id)}.`,
    onDragCancel: ({ active }) => `Reordering cancelled. ${nameOf(active.id)} stayed where it was.`,
  }

  function handleDragEnd({ active, over }: DragEndEvent) {
    if (over === null) return

    const reordered = moveInOrder(ids, Number(active.id), Number(over.id))
    if (reordered !== null) reorder.mutate(reordered)
  }

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 'var(--space-4)',
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h1 className="page-title">Locations</h1>
          <p className="page-lede">
            Where equipment is stored. Stocktakes walk these in order — the list below is that order
            {isAdmin && <>, and dragging a card by its grip is how you change it</>}.
          </p>
        </div>

        {/* Removed places are kept rather than destroyed (issue #9), and
            managing them is admin work, so crew are not sent to the screen. */}
        {isAdmin && (
          <Link to="/locations/trash" className="btn btn-secondary">
            Trash
          </Link>
        )}
      </div>

      {isAdmin && (
        <form
          className="filter-bar"
          onSubmit={(event) => {
            event.preventDefault()
            if (newName.trim() === '') return
            create.mutate({ name: newName.trim() }, { onSuccess: () => setNewName('') })
          }}
        >
          <div className="field field-inline" style={{ flex: '1 1 260px' }}>
            <label htmlFor="new-location">New location</label>
            <input
              id="new-location"
          autoComplete="off"
          data-1p-ignore="true"
          data-lpignore="true"
              className="input"
              placeholder="e.g. Dimmer Room"
              value={newName}
              onChange={(event) => setNewName(event.target.value)}
            />
          </div>
          <button type="submit" className="btn btn-primary" disabled={create.isPending}>
            Add location
          </button>
        </form>
      )}

      <ErrorNote error={create.error ?? remove.error ?? reorder.error} />

      {isAdmin ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToParentElement]}
          accessibility={{ announcements }}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={ids} strategy={rectSortingStrategy}>
            <div className="card-grid">
              {locations.map((location, index) => (
                <SortableLocationCard
                  key={location.id}
                  location={location}
                  index={index}
                  isAdmin
                  onRemove={setConfirming}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <div className="card-grid">
          {locations.map((location, index) => (
            <LocationCard
              key={location.id}
              location={location}
              index={index}
              isAdmin={false}
              onRemove={setConfirming}
            />
          ))}
        </div>
      )}

      {confirming !== null && (
        <ConfirmDialog
          title="Remove this location?"
          body={removalBody(confirming)}
          confirmLabel="Remove"
          busy={remove.isPending}
          onCancel={() => setConfirming(null)}
          onConfirm={() => {
            remove.mutate(confirming.id, { onSuccess: () => setConfirming(null) })
          }}
        />
      )}
    </>
  )
}
